import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
} from '@material-ui/core';

import api from '../../../../services/api';

// Aba "Armazém" do Painel de Gestão (B5 · Onda 1 aprovada).
// Produtividade por operador via trilha de eventos do 4LogX Operation.

interface IOperator {
  name: string;
  separations: number;
  partials: number;
  expeditions: number;
  receipts: number;
  damagedReceipts: number;
  missingItems: number;
  avgMinutesPerOrder: number | null;
}

interface IWarehouse {
  operators: IOperator[];
  actionsByHour: { hour: number; count: number }[];
  missingReasons: { reason: string; count: number }[];
}

const MEDALS = ['🥇', '🥈', '🥉'];

export const WarehouseTab = ({ period }: { period: string }) => {
  const [data, setData] = useState<IWarehouse | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get(
        `/summarizers/warehouse-productivity?period=${period}`,
      );
      setData(response.data);
    } catch {
      setData({ operators: [], actionsByHour: [], missingReasons: [] });
    }
  }, [period]);

  useEffect(() => {
    setData(null);
    load();
    timer.current = setInterval(load, 60000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.operators.length === 0) {
    return (
      <Paper style={{ padding: 32, textAlign: 'center' }}>
        <Typography variant="subtitle1" gutterBottom>
          Sem eventos do armazém neste período
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Os dados desta aba vêm da bipagem do app 4LogX Operation
          (separação, expedição e entrada). Assim que o piloto rodar,
          a produtividade por operador aparece aqui sozinha.
        </Typography>
      </Paper>
    );
  }

  // janela de horas com atividade (com 1h de folga nas pontas)
  const active = data.actionsByHour.filter((h) => h.count > 0);
  const firstHour = Math.max(0, Math.min(...active.map((h) => h.hour)) - 1);
  const lastHour = Math.min(23, Math.max(...active.map((h) => h.hour)) + 1);
  const hours = data.actionsByHour.slice(firstHour, lastHour + 1);
  const maxHour = Math.max(1, ...hours.map((h) => h.count));
  const peakHour = hours.reduce((a, b) => (b.count > a.count ? b : a), hours[0]);
  const maxReason = Math.max(1, ...data.missingReasons.map((r) => r.count));

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Box display="flex" style={{ gap: 14 }} flexWrap="wrap">
        {data.operators.map((op, index) => (
          <Paper
            key={op.name}
            style={{ flex: '1 1 260px', padding: '14px 18px', minWidth: 240 }}
          >
            <span style={{ float: 'right', fontSize: 18 }}>
              {MEDALS[index] || ''}
            </span>
            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
              {op.name}
            </Typography>
            <Typography variant="h4" style={{ fontWeight: 800, margin: '4px 0' }}>
              {op.separations + op.expeditions + op.receipts}
              <span style={{ fontSize: 13, color: '#5A6B7A', fontWeight: 600 }}>
                {' '}ações
              </span>
            </Typography>
            <Typography variant="caption" color="textSecondary" component="div">
              {op.separations} separações{op.partials > 0 && ` (${op.partials} parciais)`} ·{' '}
              {op.expeditions} expedições · {op.receipts} entradas
              {op.damagedReceipts > 0 && ` (${op.damagedReceipts} c/ avaria)`}
            </Typography>
            <Typography variant="caption" color="textSecondary" component="div">
              Tempo médio/pedido:{' '}
              <b>
                {op.avgMinutesPerOrder === null ? '—' : `${op.avgMinutesPerOrder} min`}
              </b>{' '}
              · Faltas: <b>{op.missingItems}</b>
            </Typography>
          </Paper>
        ))}
      </Box>

      <Box display="flex" style={{ gap: 16 }} flexWrap="wrap">
        <Paper style={{ flex: '2 1 480px', padding: 16 }}>
          <Typography variant="subtitle2" gutterBottom>
            Ritmo do armazém — ações por hora
          </Typography>
          <Box
            display="flex"
            alignItems="flex-end"
            style={{ gap: 5, height: 120 }}
          >
            {hours.map((h) => (
              <Box
                key={h.hour}
                flex={1}
                display="flex"
                flexDirection="column"
                justifyContent="flex-end"
                height="100%"
              >
                <div
                  title={`${h.hour}h — ${h.count} ações`}
                  style={{
                    height: `${Math.max((h.count / maxHour) * 100, h.count > 0 ? 4 : 2)}%`,
                    background:
                      h.hour === peakHour.hour && h.count > 0
                        ? '#48AE3D'
                        : h.count > 0
                          ? '#0E52A0'
                          : '#EDF1F5',
                    borderRadius: '4px 4px 0 0',
                  }}
                />
              </Box>
            ))}
          </Box>
          <Box display="flex" style={{ gap: 5 }}>
            {hours.map((h) => (
              <Typography
                key={h.hour}
                variant="caption"
                style={{ flex: 1, textAlign: 'center', color: '#5A6B7A', fontSize: 10 }}
              >
                {h.hour}h
              </Typography>
            ))}
          </Box>
          <Typography variant="caption" color="textSecondary">
            Pico às {peakHour.hour}h (verde). Soma separações, expedições e entradas.
          </Typography>
        </Paper>

        <Paper style={{ flex: '1 1 300px', padding: 16, alignSelf: 'flex-start' }}>
          <Typography variant="subtitle2" gutterBottom>
            Faltas registradas por motivo
          </Typography>
          {data.missingReasons.map((r) => (
            <Box key={r.reason} display="flex" alignItems="center" style={{ gap: 8 }} mb={1}>
              <span style={{ width: 120, fontSize: 12, color: '#5A6B7A' }}>{r.reason}</span>
              <div
                style={{
                  flex: 1,
                  background: '#EDF1F5',
                  borderRadius: 4,
                  height: 12,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: '0 auto 0 0',
                    width: `${(r.count / maxReason) * 100}%`,
                    background: '#9A6A0B',
                    borderRadius: 4,
                  }}
                />
              </div>
              <b style={{ width: 28, textAlign: 'right', fontSize: 12 }}>{r.count}</b>
            </Box>
          ))}
          {data.missingReasons.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Nenhuma falta registrada no período. ✔
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary">
            Motivos vêm da bipagem do 4LogX Operation (separação parcial).
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};
